import React, { useState, useEffect, useRef } from 'react';
import { useClient } from 'urql';
import { useSelector } from 'react-redux';
import { GET_DOCUMENT_FILE_URL } from '../../graphql/documents';
import { exportToExcel, exportRendicionesToExcel } from '../../utils/exportToExcel';
import { toAbsoluteDownloadUrl } from '../../utils/toAbsoluteDownloadUrl';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { useReportClientExport } from '../../graphql/exportAudit';
import {
  EXPORT_MODULE_BY_RESOURCE,
  resolveExportMaskedFlag,
  checkHasExportAccess,
} from '../../utils/exportPermissions';
import useBlockedFeatures from '../../hooks/useBlockedFeatures';
import './ExportActions.css';

/**
 * Reusable export actions component
 * Shows buttons to export data in different formats (PDF, XML, Excel)
 *
 * WOW-845: Excel requiere permiso export_* y reportClientExport antes de generar.
 */
const ExportActions = ({
  excelData = null,
  excelColumns = null,
  excelFileName = null,
  documentData = null,
  exportType = 'rendiciones',
  exportResource = null,
  pdfUrl = null,
  xmlUrl = null,
  disabled = false,
  onBeforeExport = null,
  onAfterExport = null,
  showExcel = true,
  showPdf = true,
  showXml = true
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const client = useClient();
  const { selectedCompany } = useSelector((state) => state.companyList);
  const { roles } = useSelector((state) => state.auth);
  const { hasModuleAccess } = useModulePermissions();
  const { blockedFeatures, userBlockedFeatures } = useBlockedFeatures();
  const [, reportClientExport] = useReportClientExport();

  const resourceKey = exportResource || (exportType === 'rendiciones' ? 'rendiciones' : 'documentos');
  const exportModuleId = EXPORT_MODULE_BY_RESOURCE[resourceKey] || 'export_documentos';
  const hasExportAccess = (id) =>
    checkHasExportAccess(id, { hasModuleAccess, blockedFeatures, userBlockedFeatures });
  const canExportExcel = hasExportAccess(exportModuleId);

  const documentId = documentData?.id || documentData?._id || null;

  const proxyUrls = useRef({ pdf: null, xml: null, html: null });
  const [urlsReady, setUrlsReady] = useState(false);

  useEffect(() => {
    if (!documentId) return;
    let cancelled = false;

    const prefetch = async (fileType) => {
      try {
        const companyId = selectedCompany?.id;
        if (!companyId) return;
        const result = await client.query(GET_DOCUMENT_FILE_URL, { documentId, fileType, companyId }).toPromise();
        const path = result?.data?.getDocumentFileUrl;
        if (!cancelled && path) {
          proxyUrls.current[fileType] = toAbsoluteDownloadUrl(path);
        }
      } catch (e) {
        // silencioso
      }
    };

    const loadAll = async () => {
      const isHtml = !documentData?.meta?.download?.pdf?.url && documentData?.meta?.download?.html?.url;
      await Promise.all([prefetch(isHtml ? 'html' : 'pdf'), prefetch('xml')]);
      if (!cancelled) setUrlsReady(true);
    };

    loadAll();
    return () => { cancelled = true; };
  }, [documentId]);

  const openDocumentFile = (fileType) => {
    const url = proxyUrls.current[fileType];
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleExcelExport = async () => {
    try {
      if (!canExportExcel) {
        console.warn('Sin permiso de exportación', exportModuleId);
        return;
      }

      if (onBeforeExport) {
        onBeforeExport();
      }

      setIsExporting(true);

      const companyId = selectedCompany?.id || documentData?.companyId;
      if (companyId) {
        const masked = resolveExportMaskedFlag({ roles, hasExportAccess });
        const gate = await reportClientExport({
          input: {
            companyId: String(companyId),
            resource: resourceKey,
            exportModuleId,
            rowCount: Array.isArray(excelData) ? excelData.length : undefined,
            masked,
          },
        });
        if (gate.error || !gate.data?.reportClientExport?.allowed) {
          console.error('Exportación denegada o error de auditoría', gate.error);
          if (onAfterExport) onAfterExport(false);
          return;
        }
      }

      let success = false;

      if (exportType === 'rendiciones' && documentData) {
        const documentType = documentData.meta?.summary ||
                           documentData.meta?.typeLabel ||
                           'documento';
        success = exportRendicionesToExcel(documentData, documentType);
      } else if (exportType === 'custom' && excelData) {
        success = exportToExcel({
          rows: excelData,
          columns: excelColumns,
          fileName: excelFileName
        });
      } else {
        console.warn('No data provided for Excel export');
      }

      if (onAfterExport) {
        onAfterExport(success);
      }
    } catch (error) {
      console.error('Error during Excel export:', error);
      if (onAfterExport) {
        onAfterExport(false);
      }
    } finally {
      setIsExporting(false);
    }
  };

  const handlePdfDownload = () => {
    const isHtml = !documentData?.meta?.download?.pdf?.url && documentData?.meta?.download?.html?.url;
    openDocumentFile(isHtml ? 'html' : 'pdf');
  };

  const handleXmlDownload = () => {
    openDocumentFile('xml');
  };

  const hasExcelData = (exportType === 'rendiciones' && documentData) ||
                       (exportType === 'custom' && excelData && excelData.length > 0);

  const isExcelDisabled = disabled || !hasExcelData || isExporting || !canExportExcel;

  const shouldShowPdf = showPdf && pdfUrl;
  const shouldShowXml = showXml && xmlUrl;
  const shouldShowExcel = showExcel && canExportExcel;

  if (!shouldShowPdf && !shouldShowXml && !shouldShowExcel) {
    return null;
  }

  return (
    <div className="export-actions">
      <div className="export-label">Exportar:</div>

      {shouldShowPdf && (
        <button
          className="btn btn-export-pdf"
          onClick={handlePdfDownload}
          disabled={disabled || !urlsReady}
          aria-label="Exportar a PDF"
          aria-busy={!urlsReady}
        >
          <span className="btn-export-content">
            <span>PDF</span>
            {!urlsReady && <span className="btn-spinner" aria-hidden="true" />}
          </span>
        </button>
      )}

      {shouldShowXml && (
        <button
          className="btn btn-export-xml"
          onClick={handleXmlDownload}
          disabled={disabled || !urlsReady}
          aria-label="Exportar a XML"
          aria-busy={!urlsReady}
        >
          <span className="btn-export-content">
            <span>XML</span>
            {!urlsReady && <span className="btn-spinner" aria-hidden="true" />}
          </span>
        </button>
      )}

      {shouldShowExcel && (
        <button
          className="btn btn-export-excel"
          onClick={handleExcelExport}
          disabled={isExcelDisabled}
          aria-label="Exportar a Excel"
          aria-busy={isExporting}
          title={!hasExcelData ? 'No hay datos para exportar' : 'Exportar a Excel'}
        >
          {isExporting ? 'Generando...' : 'Excel'}
        </button>
      )}
    </div>
  );
};

export default ExportActions;
